import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from "sprint-common-web";

import { RouteItem } from "../../../../../core/routing/ProtectedRoute";

export interface HeaderBreadcrumbProps {
    routes: HeaderBreadcrumbRoutes;
    isLoading?: boolean;
    resolveCustomPathText?(pathPart: string): string | undefined;
}

export interface HeaderBreadcrumbRoutes {
    routeItems: RouteItem[];
    baseRouteNames: string[];
}

export const HeaderBreadcrumb: React.FunctionComponent<HeaderBreadcrumbProps> = (props: HeaderBreadcrumbProps) => {
    const { routes, isLoading } = props;

    const location = useLocation();
    const history = useHistory();

    const [items, setItems] = useState<BreadcrumbItem[]>([]);

    useEffect(() => {
        setItems(createBreadcrumbItems());
    }, []);

    const createBreadcrumbItems = (): BreadcrumbItem[] => {
        const actualLocation = getSplitedPath(location.pathname);
        const breadcrumbItems: BreadcrumbItem[] = [
            ...routes.baseRouteNames.map((name: string) => {
                return { key: name, text: name };
            })
        ];

        let path = "";

        actualLocation.forEach((partialPath: string) => {
            path = path.concat(`/${partialPath}`);
            const item = getItemByPath(path);

            if (item) {
                breadcrumbItems.push(item);
            }
        });

        return breadcrumbItems;
    };

    const getItemByPath = (path: string): BreadcrumbItem | undefined => {
        const breadCrumbItem = getBreadcrumbItemByExactPath(path);

        if (breadCrumbItem) {
            return breadCrumbItem;
        }

        for (const routeItem of routes.routeItems) {
            if (doesRouteItemMatchPath(routeItem, path)) {
                const breadCrumbText = resolveTextFromRouteItem(routeItem);

                return {
                    key: routeItem.path ?? "",
                    text: breadCrumbText,

                    onClick: () => {
                        history.push(path ?? "");
                    }
                };
            }
        }

        return undefined;
    };

    const getBreadcrumbItemByExactPath = (path: string): BreadcrumbItem | undefined => {
        const item = routes.routeItems.find((routeItem: RouteItem) => routeItem.path === path);

        if (item) {
            return {
                key: item.path,
                text: item.name,
                onClick: () => {
                    history.push(path);
                }
            };
        }

        return undefined;
    };

    const doesRouteItemMatchPath = (routeItem: RouteItem, path: string): boolean => {
        const splitedPath = getSplitedPath(path);
        const splitedRouteItemPath = getSplitedPath(routeItem.path ?? "");

        if (splitedPath.length === splitedRouteItemPath.length) {
            return doesRouteItemPathMatchPath(splitedRouteItemPath, splitedPath);
        }

        return false;
    };

    const doesRouteItemPathMatchPath = (splitedRouteItemPath: string[], splitedPath: string[]): boolean => {
        let isMatched = true;

        splitedRouteItemPath.forEach((routeItemPathPart: string, index: number) => {
            if (routeItemPathPart[0] !== ":" && routeItemPathPart !== splitedPath[index]) {
                isMatched = false;
            }
        });

        return isMatched;
    };

    const resolveTextFromRouteItem = (routeItem: RouteItem): string => {
        const splitedRouteItemPath = getSplitedPath(routeItem.path ?? "");

        const lastElement = splitedRouteItemPath.pop();

        if (lastElement && lastElement[0] === ":") {
            return props.resolveCustomPathText
                ? props.resolveCustomPathText(lastElement) ?? routeItem.name
                : routeItem.name;
        }

        return routeItem.name;
    };

    const getSplitedPath = (path: string): string[] => {
        return path.split("/").filter((partialPath: string) => partialPath !== "") ?? [];
    };

    return <Breadcrumb items={items} isLoading={isLoading} />;
};
